import type { PriceAlert } from './stockTypes';

/**
 * 한 종목에 걸린 알림을 한 줄로 요약한다. (2026-09-03 사용자 요청)
 *
 * 🔴 왜 필요해졌나
 *   한 종목에 최대 5개를 걸 수 있게 되면서(V123) 관심종목 목록이
 *   그중 하나만 찍고 나머지를 숨기고 있었다. 목록은 좁아서 다 펼칠 수 없으므로
 *   "무엇을 대표로 보여줄 것인가" 를 정해야 한다.
 *
 *   대표는 **곧 울릴 것** 이다. 현재가에서 가장 가까운, 아직 살아 있는 알림.
 *   기준가가 제일 높은 것도 제일 낮은 것도 아니다 - 지금 행동을 부르는 값이
 *   하나 있다면 그것이다.
 *
 * 화면과 갈라 둔 이유는 이 규칙을 목록과 표 두 곳에서 쓰고,
 * 눈으로는 "가장 가까운 것" 이 맞게 골렸는지 검증할 수 없기 때문이다.
 */
export interface AlertSummary {
  /** 걸린 알림 수. 울려서 꺼진 것도 센다 - 모달을 열면 그것도 줄로 보인다 */
  total: number;
  /** 아직 살아 있는 알림 수 */
  active: number;
  /**
   * 대표 알림. 현재가에 가장 가까운 살아 있는 것.
   *
   * 현재가를 모르면 기준가가 가장 높은 것을 쓴다 - 목록의 첫 줄과 같아
   * 모달을 열었을 때 눈이 이어진다. 전부 꺼져 있으면 null 이다.
   */
  nearest: PriceAlert | null;
  /** 대표 알림까지 남은 거리(%). 현재가를 모르면 null */
  nearestGapPercent: number | null;
  /** 걸어둔 것은 있는데 전부 울려 꺼졌다. 화면이 🔕 로 구분한다 */
  allTriggered: boolean;
}

/**
 * @param alerts       그 종목의 알림들. 순서는 상관없다
 * @param currentPrice 현재가. 없으면 거리 계산을 건너뛴다
 */
export function summarizeAlerts(
  alerts: PriceAlert[],
  currentPrice: number | null,
): AlertSummary {
  const active = alerts.filter((alert) => alert.active);

  const empty: AlertSummary = {
    total: alerts.length,
    active: active.length,
    nearest: null,
    nearestGapPercent: null,
    // 🔴 하나도 안 걸린 것과 전부 울린 것은 다른 상태다.
    //   둘을 같은 모양으로 두면 재설정할 때를 놓친다
    allTriggered: alerts.length > 0 && active.length === 0,
  };

  if (active.length === 0) {
    return empty;
  }

  if (currentPrice === null || currentPrice === 0) {
    // 거리를 못 재면 기준가 내림차순의 첫 줄을 대표로 쓴다
    const top = [...active].sort((a, b) => b.targetPrice - a.targetPrice)[0] ?? null;
    return { ...empty, nearest: top, nearestGapPercent: null };
  }

  let nearest = active[0] ?? null;
  let best = Number.POSITIVE_INFINITY;
  for (const alert of active) {
    const gap = Math.abs((alert.targetPrice - currentPrice) / currentPrice) * 100;
    if (gap < best) {
      best = gap;
      nearest = alert;
    }
  }

  return { ...empty, nearest, nearestGapPercent: best };
}
